// updatePruefung.ts — Erkennt, dass eine neuere KMVS-Version ausgeliefert ist
// (User 28.09.2026: „die Leute sollen nicht immer neu laden müssen").
//
// Der Build legt `/version.json` neben index.html (vite.config.ts); die
// laufende App kennt ihre eigene Version aus `VITE_APP_VERSION`. Weichen beide
// ab, ist ein Update da. Geprüft wird alle paar Minuten und immer dann, wenn
// der Tab wieder in den Vordergrund kommt. Zusätzlich gilt ein fehlgeschlagenes
// Nachladen eines Code-Teils (`vite:preloadError`) als sicheres Zeichen: Nach
// einem Deploy sind die alten Dateien weg.

import * as React from "react";

export const PRUEF_INTERVALL_MS = 5 * 60_000;
/** „Später" blendet den Hinweis so lange aus. */
export const SPAETER_MS = 30 * 60_000;

/** Pure: Ist die ausgelieferte Version eine andere als die laufende? */
export function istNeuereVersion(laufend: string | undefined, ausgeliefert: unknown): boolean {
  const neu = (ausgeliefert as { version?: unknown } | null)?.version;
  return typeof neu === "string" && !!neu && !!laufend && neu !== laufend;
}

async function ausgelieferteVersion(): Promise<unknown> {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store", credentials: "omit" });
  if (!res.ok) return null;
  return res.json();
}

export function useUpdateVerfuegbar(
  laufend: string | undefined = import.meta.env.VITE_APP_VERSION,
  aktiv: boolean = import.meta.env.PROD,
): { verfuegbar: boolean; spaeter: () => void } {
  const [verfuegbar, setVerfuegbar] = React.useState(false);
  const [ruheBis, setRuheBis] = React.useState(0);
  const [jetzt, setJetzt] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!aktiv) return;
    let aus = false;
    const pruefen = async () => {
      setJetzt(Date.now());
      try {
        if (istNeuereVersion(laufend, await ausgelieferteVersion()) && !aus) setVerfuegbar(true);
      } catch {
        /* offline o. Ä. — beim nächsten Mal */
      }
    };
    const sichtbar = () => {
      if (document.visibilityState === "visible") void pruefen();
    };
    const ladefehler = () => setVerfuegbar(true);
    void pruefen();
    const t = window.setInterval(pruefen, PRUEF_INTERVALL_MS);
    document.addEventListener("visibilitychange", sichtbar);
    window.addEventListener("focus", sichtbar);
    window.addEventListener("vite:preloadError", ladefehler);
    return () => {
      aus = true;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", sichtbar);
      window.removeEventListener("focus", sichtbar);
      window.removeEventListener("vite:preloadError", ladefehler);
    };
  }, [laufend, aktiv]);

  return {
    verfuegbar: verfuegbar && jetzt >= ruheBis,
    spaeter: () => {
      const bis = Date.now() + SPAETER_MS;
      setRuheBis(bis);
      setJetzt(Date.now());
    },
  };
}
